import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api, fmtINR, STATUS_COLORS, STATUS_LABEL, formatApiError, ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, FileArrowUp, Paperclip, Trash } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import WhatsAppNudge from "@/components/WhatsAppNudge";

const PAY_MODES = ["Bank Transfer", "NEFT", "RTGS", "IMPS", "UPI", "Cash", "Cheque"];

export default function ExpenseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // Origin-aware navigation: if the detail page was opened from MyQueue (or
  // any other list), send the user back there on "Back" and after a
  // successful Approve. Falls back to /expenses when opened directly.
  const backTo = location.state?.from || "/expenses";
  const fileInputRef = useRef(null);
  const [replacingFileId, setReplacingFileId] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [exp, setExp] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [showPay, setShowPay] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpMask, setOtpMask] = useState("");
  const [otpHint, setOtpHint] = useState("");
  const [pay, setPay] = useState({
    payment_mode: "NEFT",
    payment_date: new Date().toISOString().slice(0, 10),
    bank_account: "", utr: "", remarks: "", otp: "",
    payment_ack_file_id: null, payment_ack_name: "",
  });

  const load = () =>
    api.get(`/expenses/${id}`)
      .then((r) => { setExp(r.data); setLoadError(null); })
      .catch((e) => setLoadError(formatApiError(e.response?.data?.detail) || "Failed to load expense"));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const up = await api.post("/files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post(`/expenses/${id}/attach`, {
        file_id: up.data.id,
        filename: file.name,
        kind: "invoice",
        replace_file_id: replacingFileId,
      });
      toast.success(replacingFileId ? "Attachment replaced successfully" : "Document attached successfully");
      setReplacingFileId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to upload file");
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = async (fileId) => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await api.delete(`/expenses/${id}/attachments/${fileId}`);
      toast.success("Attachment removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to remove attachment");
    }
  };

  const triggerUpload = (replaceId = null) => {
    setReplacingFileId(replaceId);
    fileInputRef.current?.click();
  };
  useEffect(() => { load(); }, [id]);

  if (loadError) {
    return (
      <div className="p-8 max-w-xl mx-auto" data-testid="expense-detail-error">
        <div className="bg-white border border-rose-200 p-6 rounded-sm">
          <div className="text-xs uppercase tracking-widest text-rose-700 mb-2">Could not open expense</div>
          <div className="text-sm text-zinc-800">{loadError}</div>
          <div className="mt-4 flex gap-3">
            <Button onClick={load} variant="outline" className="rounded-sm" data-testid="expense-retry-btn">Retry</Button>
            <Button onClick={() => nav(backTo)} className="rounded-sm bg-zinc-900 text-white" data-testid="expense-back-btn">Back</Button>
          </div>
        </div>
      </div>
    );
  }
  if (!exp) return (
    <div className="p-8 text-zinc-500 flex items-center gap-2" data-testid="expense-detail-loading">
      <span className="inline-block w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
      Loading expense…
    </div>
  );

  // A user can approve if they are next in chain (or admin)
  const nextRole = exp.next_approver_role;
  const canApprove = ["submitted", "resubmitted"].includes(exp.status) &&
                     (user.role === "admin" || user.role === nextRole);
  const canPay = ["finance", "admin"].includes(user.role) && exp.status === "approved";

  // Sent-back records can be edited & resubmitted by the creator, any centre_manager
  // of that centre, and admins. Backend enforces centre-scope; we allow the button
  // to appear for any of these roles so the request round-trips cleanly.
  const canResubmit = exp.status === "sent_back" &&
    (user.role === "admin" || user.role === "centre_manager" || user.id === exp.created_by);
  const resubmit = async () => {
    // Payload = current values (user can edit description/remarks inline first)
    const payload = {
      centre_id: exp.centre_id,
      expense_date: exp.expense_date,
      category: exp.category, sub_category: exp.sub_category || null,
      vendor: exp.vendor || null,
      invoice_number: exp.invoice_number || null,
      invoice_date: exp.invoice_date || null,
      description: (window.prompt("Update description (mandatory):", exp.description || "") || "").trim(),
      amount_before_gst: exp.amount_before_gst || 0,
      cgst: exp.cgst || 0, sgst: exp.sgst || 0, igst: exp.igst || 0,
      total_amount: exp.total_amount, tds: exp.tds || 0,
      net_payable: exp.net_payable,
      payment_type: exp.payment_type || null,
      expense_type: exp.expense_type || null,
      budget_head: exp.budget_head || null,
      remarks: exp.remarks || null,
      attachments: exp.attachments || [],
    };
    if (!payload.description) return toast.error("Description is mandatory.");
    try {
      await api.post(`/expenses/${id}/resubmit`, payload);
      toast.success("Resubmitted — approval workflow restarted from Level 1");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const act = async (action) => {
    if ((action === "reject" || action === "send-back") && !remarks.trim()) {
      return toast.error("Please enter remarks");
    }
    try {
      await api.post(`/expenses/${id}/${action}`, { remarks });
      toast.success(`Expense ${action.replace("-", " ")}`);
      setRemarks("");
      if (action === "approve" && location.state?.from) {
        // Redirect back to origin (e.g. My Queue) so the approved item drops
        // out of the pending list and the queue refreshes automatically.
        nav(location.state.from, { replace: true });
        return;
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitPay = async () => {
    if (!pay.otp || pay.otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    try {
      await api.post(`/expenses/${id}/pay`, pay);
      toast.success("Payment recorded");
      setShowPay(false);
      setOtpSent(false);
      setOtpHint("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const requestOtp = async () => {
    try {
      const r = await api.post(`/expenses/${id}/pay/otp`);
      setOtpSent(true);
      setOtpMask(r.data.sent_to);
      setOtpHint(r.data.demo_hint || "");
      toast.success(`OTP sent to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openPay = () => {
    setShowPay(true);
    setOtpSent(false);
    setOtpHint("");
    setPay({ ...pay, otp: "" });
  };

  const isPending = ["submitted", "resubmitted", "in_review"].includes(exp.status);

  return (
    <div className="p-4 lg:p-8 max-w-6xl space-y-6" data-testid="expense-detail-root">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <button onClick={() => nav(backTo)} className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black">← Back</button>
          <div className="flex items-center flex-wrap gap-2 sm:gap-3 mt-2">
            <h1 className="font-heading text-2xl sm:text-3xl tabular-nums tracking-tight">{exp.expense_no}</h1>
            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${STATUS_COLORS[exp.status]}`}>{STATUS_LABEL[exp.status]}</span>
            {exp.duplicate_flag && <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-amber-100 text-amber-900 border border-amber-300">Possible Duplicate</span>}
          </div>
          <div className="text-sm text-zinc-500 mt-1 break-words">{exp.centre_code} · {exp.centre_name} · {exp.expense_date}</div>
        </div>
        <div className="sm:text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Amount</div>
          <div className="text-2xl sm:text-3xl font-heading tabular-nums">{fmtINR(exp.total_amount)}</div>
          <div className="text-xs text-zinc-500 tabular-nums">Net Payable: {fmtINR(exp.net_payable)}</div>
          {isPending && (
            <div className="mt-3 sm:flex sm:justify-end">
              <WhatsAppNudge kind="expense" id={exp.id} number={exp.expense_no} />
            </div>
          )}
        </div>
      </div>

      {/* Sent-back banner + Resubmit (spec §25) */}
      {exp.status === "sent_back" && (
        <div className="bg-amber-50 border-2 border-amber-400 p-5 rounded-sm" data-testid="expense-sent-back-banner">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-amber-800">Sent Back — action required</div>
              <div className="font-heading text-lg mt-1 text-amber-900">Please review and resubmit</div>
              {exp.sent_back_reason && (
                <div className="mt-2 text-sm text-zinc-800"><strong>Reason:</strong> {exp.sent_back_reason}</div>
              )}
              <div className="mt-2 text-xs text-zinc-600">
                {(exp.history || []).filter(h => h.action === "sent_back").slice(-1).map((h) => (
                  <span key={h.at || h.by}>Sent back by <b>{h.by}</b> · {h.at?.slice(0,19).replace("T", " ")}</span>
                ))}
              </div>
            </div>
            {canResubmit && (
              <Button
                onClick={resubmit}
                className="rounded-sm bg-amber-700 hover:bg-amber-800 text-white gap-2"
                data-testid="expense-resubmit-btn"
              >
                <ArrowRight size={14} weight="bold" /> Edit & Resubmit
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Details grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Vendor & Invoice</div>
          <dl className="text-sm space-y-2">
            <Row k="Vendor" v={exp.vendor} />
            <Row k="Invoice No" v={exp.invoice_number} />
            <Row k="Invoice Date" v={exp.invoice_date} />
            <Row k="Category" v={exp.category} />
            <Row k="Sub-category" v={exp.sub_category} />
            <Row k="Budget Head" v={exp.budget_head} />
            <Row k="Description" v={exp.description} />
          </dl>
        </div>
        <div className="bg-white border border-zinc-200 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Financial Breakdown</div>
          <dl className="text-sm space-y-2 tabular-nums">
            <Row k="Amount before GST" v={fmtINR(exp.amount_before_gst)} />
            <Row k="CGST" v={fmtINR(exp.cgst)} />
            <Row k="SGST" v={fmtINR(exp.sgst)} />
            <Row k="IGST" v={fmtINR(exp.igst)} />
            <Row k="Payment Type" v={exp.payment_type} />
          </dl>
          {/* Gross → TDS → Net Payable summary (matches Expense form) */}
          <div className="mt-4 pt-4 border-t border-zinc-200 space-y-1 text-sm tabular-nums"
               data-testid="expense-detail-totals">
            <div className="flex justify-between font-medium text-zinc-900">
              <span>Gross Total (incl. GST)</span>
              <span data-testid="detail-gross">{fmtINR(exp.total_amount)}</span>
            </div>
            <div className="flex justify-between text-rose-700">
              <span>Less: TDS {exp.tds_pct ? `(${exp.tds_pct}%)` : ""}</span>
              <span data-testid="detail-tds">− {fmtINR(exp.tds || 0)}</span>
            </div>
            <div className="flex justify-between font-heading text-lg text-emerald-800 border-t-2 border-zinc-900 pt-2 mt-2">
              <span>Net Payable</span>
              <span data-testid="detail-net">{fmtINR(exp.net_payable)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Attachments */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Documents &amp; Invoices</div>
            <div className="font-heading text-lg mt-1">Attachments</div>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
              data-testid="expense-upload-input"
            />
            <Button
              onClick={() => triggerUpload(null)}
              disabled={uploadingDoc}
              data-testid="expense-upload-btn"
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-9 gap-2 text-xs"
            >
              <FileArrowUp size={14} weight="bold" />
              {uploadingDoc ? "Uploading…" : "Upload Document"}
            </Button>
          </div>
        </div>

        {(exp.attachments || []).length === 0 ? (
          <p className="text-xs text-zinc-500">No documents attached yet.</p>
        ) : (
          <ul className="space-y-2">
            {(exp.attachments || []).map((a, i) => {
              const fileId = a.id || a.file_id;
              const fileName = a.original_filename || a.filename || "document";
              return (
                <li key={fileId || i} className="flex items-center justify-between p-3 border border-zinc-200 rounded-sm bg-zinc-50/50 hover:bg-zinc-50">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Paperclip size={16} className="text-zinc-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{fileName}</div>
                      <div className="text-xs text-zinc-500">
                        {a.kind || "attachment"} {a.uploaded_by ? `· uploaded by ${a.uploaded_by}` : ""} {a.uploaded_at ? `· ${new Date(a.uploaded_at).toLocaleString()}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs uppercase tracking-widest text-zinc-800 hover:text-black border border-zinc-300 hover:border-zinc-800 px-2.5 py-1 bg-white"
                      data-testid={`expense-attachment-view-${i}`}
                    >
                      View →
                    </a>
                    <button
                      type="button"
                      onClick={() => triggerUpload(fileId)}
                      disabled={uploadingDoc}
                      className="text-xs uppercase tracking-widest text-emerald-700 hover:text-emerald-900 border border-emerald-300 hover:border-emerald-700 px-2.5 py-1 bg-white"
                      title="Upload a new copy of this file"
                      data-testid={`expense-attachment-replace-${i}`}
                    >
                      Re-upload
                    </button>
                    <button
                      type="button"
                      onClick={() => removeAttachment(fileId)}
                      className="p-1 text-zinc-400 hover:text-rose-600"
                      title="Remove attachment"
                      data-testid={`expense-attachment-delete-${i}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Payment info */}
      {exp.payment && (
        <div className="bg-white border border-zinc-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Payment</div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/receipts/expense/${exp.id}.pdf`}
              target="_blank" rel="noopener noreferrer"
              data-testid="download-receipt-btn"
              className="text-[11px] uppercase tracking-widest text-zinc-800 hover:text-black border border-zinc-300 hover:border-zinc-800 px-3 py-1"
            >
              Download Receipt →
            </a>
          </div>
          <dl className="text-sm space-y-2">
            <Row k="Mode" v={exp.payment.payment_mode} />
            <Row k="Date" v={exp.payment.payment_date} />
            <Row k="Bank Account" v={exp.payment.bank_account} />
            <Row k="UTR / Reference" v={exp.payment.utr} />
            <Row k="Paid By" v={exp.paid_by_name} />
          </dl>
          {exp.payment.payment_ack_file_id && (
            <div className="mt-4 pt-4 border-t border-zinc-100">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Payment Acknowledgment
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${exp.payment.payment_ack_file_id}/download`}
                target="_blank" rel="noopener noreferrer"
                data-testid="payment-ack-link"
                className="inline-flex items-center gap-2 text-sm text-zinc-800 hover:text-black underline"
              >
                📎 Download acknowledgment
              </a>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {(canApprove || canPay) && (
        <div className="bg-white border border-zinc-200 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Actions</div>
          {canApprove && (
            <div className="space-y-3">
              <Textarea placeholder="Remarks (required for reject / send-back)"
                        value={remarks} onChange={(e) => setRemarks(e.target.value)}
                        className="rounded-sm border-zinc-300" data-testid="action-remarks" />
              <div className="flex gap-2">
                <Button onClick={() => act("approve")} data-testid="approve-btn"
                        className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white">Approve</Button>
                <Button onClick={() => act("reject")} data-testid="reject-btn"
                        className="rounded-sm bg-rose-700 hover:bg-rose-800 text-white">Reject</Button>
                <Button onClick={() => act("send-back")} data-testid="sendback-btn" variant="outline"
                        className="rounded-sm border-zinc-300">Send back for correction</Button>
              </div>
            </div>
          )}
          {canPay && (
            <Button onClick={openPay} data-testid="process-pay-btn"
                    className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">Process Payment</Button>
          )}
        </div>
      )}

      {/* Approval chain progress */}
      {exp.approval_chain && exp.approval_chain.length > 0 && (
        <div className="bg-white border border-zinc-200 p-6" data-testid="approval-chain">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Approval Chain</div>
          <div className="flex flex-wrap gap-3">
            {exp.approval_chain.map((role, i) => {
              const done = (exp.approvals_done || [])[i];
              const isNext = !done && exp.next_approver_role === role && ["submitted", "resubmitted"].includes(exp.status);
              const state = done ? "done" : isNext ? "next" : "pending";
              const cls = {
                done: "border-emerald-600 bg-emerald-50 text-emerald-900",
                next: "border-amber-500 bg-amber-50 text-amber-900",
                pending: "border-zinc-300 bg-zinc-50 text-zinc-500",
              }[state];
              return (
                <div key={`${role}-${i}`} className={`px-4 py-3 border ${cls} min-w-[180px]`}>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Step {i + 1}</div>
                  <div className="font-medium mt-1">{ROLES[role]}</div>
                  {done && <div className="text-xs mt-1">{done.name} · {new Date(done.at).toLocaleDateString()}</div>}
                  {isNext && <div className="text-xs mt-1 italic">Awaiting…</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Audit / History</div>
        <ol className="space-y-2 text-sm">
          {(exp.history || []).map((h, i) => (
            <li key={`${h.at || i}-${h.action}`} className="flex gap-4 border-l-2 border-zinc-200 pl-3">
              <span className="text-zinc-400 tabular-nums text-xs w-40">{new Date(h.at).toLocaleString()}</span>
              <span className="font-medium capitalize">{h.action}</span>
              <span className="text-zinc-600">by {h.by}</span>
              {h.remarks && <span className="text-zinc-500 italic">— {h.remarks}</span>}
            </li>
          ))}
        </ol>
      </div>

      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent className="bg-white rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">Process Payment</DialogTitle></DialogHeader>
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 px-3 py-2">
            Two-factor authentication is required. An OTP will be sent to your registered email.
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Mode</Label>
              <Select value={pay.payment_mode} onValueChange={(v) => setPay({ ...pay, payment_mode: v })}>
                <SelectTrigger className="rounded-sm h-10 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Payment Date</Label>
              <Input type="date" value={pay.payment_date}
                     onChange={(e) => setPay({ ...pay, payment_date: e.target.value })}
                     className="rounded-sm h-10 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Bank Account</Label>
              <Input value={pay.bank_account} onChange={(e) => setPay({ ...pay, bank_account: e.target.value })}
                     className="rounded-sm h-10 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">UTR / Reference</Label>
              <Input value={pay.utr} onChange={(e) => setPay({ ...pay, utr: e.target.value })}
                     className="rounded-sm h-10 mt-2" data-testid="pay-utr" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Remarks</Label>
              <Input value={pay.remarks} onChange={(e) => setPay({ ...pay, remarks: e.target.value })}
                     className="rounded-sm h-10 mt-2" />
            </div>

            <PaymentAckUploader
              value={pay.payment_ack_file_id}
              filename={pay.payment_ack_name}
              onUploaded={(fid, name) => setPay({ ...pay, payment_ack_file_id: fid, payment_ack_name: name })}
            />

            <div className="border-t border-zinc-200 pt-3">
              <Label className="text-xs uppercase tracking-widest">Two-Factor OTP</Label>
              {!otpSent ? (
                <Button type="button" onClick={requestOtp} data-testid="request-otp-btn"
                        className="mt-2 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10 w-full">
                  Send OTP to my email
                </Button>
              ) : (
                <>
                  <Input
                    value={pay.otp}
                    onChange={(e) => setPay({ ...pay, otp: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                    placeholder="6-digit code"
                    className="rounded-sm h-10 mt-2 tabular-nums text-center tracking-[0.4em] font-mono"
                    data-testid="pay-otp"
                    inputMode="numeric"
                  />
                  <div className="text-xs text-zinc-500 mt-1">Sent to {otpMask}. Expires in 10 minutes.</div>
                  {otpHint && (
                    <div className="text-xs text-amber-800 mt-1 bg-amber-50 border border-amber-200 px-2 py-1">
                      Demo mode: OTP is <span className="font-mono font-bold">{otpHint}</span> (Resend not configured)
                    </div>
                  )}
                  <button type="button" onClick={requestOtp} className="text-xs text-zinc-600 hover:text-black underline mt-1">
                    Resend OTP
                  </button>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPay(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submitPay} data-testid="confirm-pay" disabled={!otpSent}
                    className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">Mark as Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1">
      <dt className="text-zinc-500">{k}</dt>
      <dd className="text-zinc-900 text-right">{v || "—"}</dd>
    </div>
  );
}

function PaymentAckUploader({ value, filename, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/files/upload", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data.id, file.name);
      toast.success("Acknowledgment attached");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="border-t border-zinc-200 pt-3">
      <Label className="text-xs uppercase tracking-widest">Payment Acknowledgment</Label>
      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png"
        onChange={upload} className="hidden" data-testid="pay-ack-input" />
      <div className="mt-2 flex items-center gap-3">
        <Button type="button" variant="outline" onClick={() => ref.current?.click()}
          disabled={busy} data-testid="pay-ack-btn"
          className="rounded-sm h-10 gap-2 text-xs border-zinc-300">
          {busy ? "Uploading…" : value ? "Replace" : "Upload acknowledgment"}
        </Button>
        {filename && <span className="text-xs text-zinc-600 truncate">{filename}</span>}
      </div>
      <p className="text-[10px] text-zinc-500 mt-1">
        Optional — the centre manager will be able to view/download this from the expense detail.
      </p>
    </div>
  );
}
